import { useEffect, useId, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import Screen from '../../demo/Screen';
import './PatientCost.css';

/* The share of patients who should come back for follow-up. */
export const TARGET_RETURN = 50;
const CRORE = 1e7;
const LAKH = 1e5;

/*
 * Three illustrative lab archetypes (synthetic, rounded). Branded chains get
 * more patients back than small or new labs. Monthly gaps: Small ₹3.0 L,
 * Medium ₹10.0 L (the default), Large ₹48.0 L.
 */
export const ARCHETYPES = {
  small: {
    name: 'Small',
    detail: 'single centre',
    label: 'small single-centre',
    values: { reports: '2500', share: '20', bill: '400' },
  },
  medium: {
    name: 'Medium',
    detail: 'regional, several centres',
    label: 'medium regional',
    values: { reports: '10000', share: '30', bill: '500' },
  },
  large: {
    name: 'Large',
    detail: 'national chain',
    label: 'large national-chain',
    values: { reports: '100000', share: '42', bill: '600' },
  },
};
const PRESETS = ['small', 'medium', 'large'];

const prefersStill = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const DEFAULT_PRESET = 'medium';

/* Empty or non-numeric input counts as 0, so the result never shows NaN. */
function toNumber(value, max = Infinity) {
  const n = parseFloat(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(n, max);
}

/*
 * Monthly revenue lost to patients who do not come back:
 * max(0, 50% − return rate) × reports per month × average follow-up bill.
 * At or above the target the gap is 0, never negative.
 */
export function lostRevenue({ reports, share, bill }) {
  const gap = Math.max(0, TARGET_RETURN - toNumber(share, 100));
  return (gap * toNumber(reports) * toNumber(bill)) / 100;
}

/* "₹X.XX Cr" from one crore up, "₹X.X L" below it. */
export function formatLostRevenue(rupees) {
  const lakhs = Math.round(rupees / (LAKH / 10)) / 10;
  if (lakhs >= 100) return `₹${(rupees / CRORE).toFixed(2)} Cr`;
  return `₹${lakhs.toFixed(1)} L`;
}

/* Five figures; the first two (blue) are the patients who do not return. */
const PEOPLE = [
  { key: 'p1', lost: true },
  { key: 'p2', lost: true },
  { key: 'p3', lost: false },
  { key: 'p4', lost: false },
  { key: 'p5', lost: false },
];

/* A red stack of coins, as in the mockup's "High cost" tile. */
function CoinsGlyph() {
  return (
    <svg viewBox="0 0 48 48" width="40" height="40" aria-hidden="true" focusable="false">
      {[30, 21, 12].map((y) => (
        <g key={y}>
          <path d={`M8 ${y}v6c0 3 7.2 5.5 16 5.5S40 ${y + 9} 40 ${y + 6}v-6Z`} fill="#c8202c" />
          <ellipse cx="24" cy={y} rx="16" ry="5.5" fill="#ec3a45" />
          <ellipse cx="24" cy={y} rx="10" ry="2.6" fill="none" stroke="#ff9aa1" strokeWidth="1.4" />
        </g>
      ))}
    </svg>
  );
}

/* An orange group of three people, as in the mockup's "do not return" tile. */
function GroupGlyph() {
  return (
    <svg viewBox="0 0 48 48" width="42" height="42" aria-hidden="true" focusable="false">
      <circle cx="11" cy="17" r="5.2" fill="#f2a33a" />
      <circle cx="37" cy="17" r="5.2" fill="#f2a33a" />
      <path d="M2 36c0-6.5 4-11 9-11 2.6 0 4.8 1 6.3 2.8C15.4 30 14.3 33 14.2 36Z" fill="#f2a33a" />
      <path d="M46 36c0-6.5-4-11-9-11-2.6 0-4.8 1-6.3 2.8 1.9 2.2 3 5.2 3.1 8.2Z" fill="#f2a33a" />
      <circle cx="24" cy="14.5" r="6.6" fill="#e9860f" />
      <path d="M12 38c0-8 5.4-13.5 12-13.5S36 30 36 38Z" fill="#e9860f" />
    </svg>
  );
}

/* Three rising bars, white and blue, for the impact figure. */
function ImpactGlyph() {
  return (
    <svg viewBox="0 0 48 48" width="40" height="40" aria-hidden="true" focusable="false">
      <rect x="9" y="25" width="8" height="15" rx="2.5" fill="#ffffff" />
      <rect x="20" y="12" width="8" height="28" rx="2.5" fill="#ffffff" />
      <rect x="31" y="19" width="8" height="21" rx="2.5" fill="#4d8dff" />
    </svg>
  );
}

function CostChart() {
  return (
    <svg className="pcost-chart" viewBox="0 0 250 210" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="pcost-arrow" x1="0" x2="1">
          <stop offset="0" stopColor="#e5484d" stopOpacity="0.1" />
          <stop offset="1" stopColor="#e5484d" />
        </linearGradient>
      </defs>
      <rect x="10" y="130" width="50" height="50" rx="3" fill="#fbd5d6" />
      <rect x="95" y="95" width="50" height="85" rx="3" fill="#f7b4b6" />
      <rect x="180" y="55" width="50" height="125" rx="3" fill="#ee7e82" />
      <path d="M0 98 C 80 86, 160 62, 228 22" fill="none" stroke="url(#pcost-arrow)" strokeWidth="3" />
      <path d="M216 15 L238 15 L229 35 Z" fill="#e5484d" />
      <g className="pcost-chart__axis">
        <text x="35" y="204">Year 1</text>
        <text x="120" y="204">Year 2</text>
        <text x="205" y="204">Year 3</text>
      </g>
    </svg>
  );
}

function PeopleRow() {
  return (
    <div className="pcost-people" aria-hidden="true">
      <div className="pcost-people__row">
        {PEOPLE.map(({ key, lost }) => (
          <svg key={key} className={lost ? 'is-lost' : undefined} viewBox="0 0 40 52" focusable="false">
            <circle cx="20" cy="10" r="9.5" />
            <path d="M1 52V40c0-10.5 8.5-17 19-17s19 6.5 19 17v12Z" />
          </svg>
        ))}
      </div>
      <span className="pcost-people__brace" />
    </div>
  );
}

function Field({ id, label, unit, value, onChange, max }) {
  const inputId = useId();
  return (
    <div className="pcost-field">
      <label htmlFor={inputId}>
        {label}
        {unit && <span className="pcost-field__unit"> ({unit})</span>}
      </label>
      <input
        id={inputId}
        type="number"
        inputMode="numeric"
        min="0"
        max={max}
        step="1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-trapezium-id={id}
      />
    </div>
  );
}

export default function PatientCost() {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(ARCHETYPES[DEFAULT_PRESET].values);
  const [preset, setPreset] = useState(DEFAULT_PRESET);
  const calcId = useId();
  const calcRef = useRef(null);

  /*
   * Opening the calculator grows the panel downward, so bring it (and the
   * toggle under it) fully into view above the fixed step bar. Focus stays on
   * the toggle; only the page scrolls. Instant under reduced motion.
   */
  useEffect(() => {
    const calc = calcRef.current;
    if (!open || !calc || typeof calc.scrollIntoView !== 'function') return;
    calc.scrollIntoView({ block: 'nearest', behavior: prefersStill() ? 'auto' : 'smooth' });
  }, [open]);

  /* Any edit makes the numbers the visitor's own, so no preset stays active. */
  const set = (key) => (value) => {
    setPreset(null);
    setValues((v) => ({ ...v, [key]: value }));
  };
  const choose = (key) => {
    setPreset(key);
    setValues(ARCHETYPES[key].values);
  };

  const atTarget = toNumber(values.share, 100) >= TARGET_RETURN;
  const amount = atTarget ? '₹0' : formatLostRevenue(lostRevenue(values));
  const archetype = preset ? ARCHETYPES[preset] : null;
  const scale = archetype ? `a ${archetype.label} lab` : 'a lab with your numbers';
  const lead = archetype ? `For ${scale}, this can mean` : 'With your numbers, this can mean';

  return (
    <Screen id="problem.patient-cost" title="The cost of losing patients" className="pcost">
      <p className="eyebrow pcost-eyebrow">Problem · 1 of 2</p>
      <div className="pcost-layout">
        <h1 className="headline pcost-headline">
          It costs a lot to bring in new patients. <em>Losing existing ones costs more.</em>
        </h1>
        <div className="pcost-main">
          <p className="lede pcost-lede">
            Acquiring a new patient is expensive. Many patients also need to return for follow-up tests, but
            most labs are not effective at bringing them back.
          </p>

          <div className="pcost-cards">
            <section className="card pcost-card" aria-labelledby="pcost-acquire">
              <div className="pcost-card__head">
                <span className="pcost-card__icon pcost-card__icon--red" aria-hidden="true">
                  <CoinsGlyph />
                </span>
                <h2 id="pcost-acquire">
                  High cost to <span className="pcost-br">acquire new patients</span>
                </h2>
              </div>
              <div className="pcost-card__visual pcost-card__visual--chart">
                <CostChart />
                <span className="pcost-tag">Rising acquisition costs</span>
              </div>
            </section>

            <section className="card pcost-card" aria-labelledby="pcost-return">
              <div className="pcost-card__head">
                <span className="pcost-card__icon pcost-card__icon--amber" aria-hidden="true">
                  <GroupGlyph />
                </span>
                <h2 id="pcost-return">
                  Many patients <span className="pcost-br">do not return</span>
                </h2>
              </div>
              <div className="pcost-card__visual pcost-card__visual--people">
                <PeopleRow />
                <p className="pcost-ratio">
                  <strong>2 in 5</strong>
                  <span>patients who should come back, don’t.</span>
                </p>
              </div>
            </section>
          </div>
        </div>

        <aside className="pcost-side" aria-labelledby="pcost-impact">
          <section className="pcost-impact">
            <p className="pcost-impact__eyebrow">The impact</p>
            <h2 id="pcost-impact">This adds up to significant revenue loss every month.</h2>

            <div
              className="pcost-result"
              aria-live="polite"
              aria-atomic="true"
              data-trapezium-state={open ? 'calculated' : undefined}
            >
              <span className="pcost-result__icon" aria-hidden="true">
                <ImpactGlyph />
              </span>
              <p>
                <span className="pcost-result__lead">{lead}</span>
                <span className="pcost-result__figure">
                  <strong className="pcost-result__amount">{amount}</strong>
                  {atTarget ? (
                    <span className="pcost-result__per pcost-result__per--ok"> — you’re already at the target</span>
                  ) : (
                    <span className="pcost-result__per"> every month</span>
                  )}
                </span>
                <span className="pcost-result__tail">
                  {atTarget
                    ? 'At least half of your patients come back for follow-up, so there is no gap to close.'
                    : 'in lost follow-up revenue*, from patients who do not come back.'}
                </span>
              </p>
            </div>

            {open && (
              <div className="pcost-calc" id={calcId} ref={calcRef} role="group" aria-label="Your numbers">
                <div className="pcost-presets" role="group" aria-label="Start from a lab like yours">
                  {PRESETS.map((key) => {
                    const a = ARCHETYPES[key];
                    return (
                      <button
                        key={key}
                        type="button"
                        className="pcost-preset"
                        aria-pressed={preset === key}
                        onClick={() => choose(key)}
                        data-trapezium-id={`problem.patient-cost.preset-${key}`}
                      >
                        <span className="pcost-preset__name">{a.name}</span>
                        <span className="pcost-preset__detail"> · {a.detail}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="pcost-target">
                  <strong>{TARGET_RETURN}%</strong> patients who should come back for follow-up
                </p>
                <div className="pcost-fields">
                  <Field
                    id="problem.patient-cost.input-reports"
                    label="Reports per month"
                    value={values.reports}
                    onChange={set('reports')}
                  />
                  <Field
                    id="problem.patient-cost.input-share"
                    label="% of patients who come back"
                    max="100"
                    value={values.share}
                    onChange={set('share')}
                  />
                  <Field
                    id="problem.patient-cost.input-bill"
                    label="Average follow-up bill"
                    unit="₹"
                    value={values.bill}
                    onChange={set('bill')}
                  />
                </div>
              </div>
            )}

            <button
              type="button"
              className="pcost-own"
              aria-expanded={open}
              aria-controls={open ? calcId : undefined}
              onClick={() => setOpen((o) => !o)}
              data-trapezium-id="problem.patient-cost.open-calculator"
            >
              Use your own numbers
              <ArrowRight size={18} aria-hidden="true" className="pcost-own__arrow" />
            </button>

            <span className="pcost-impact__bars" aria-hidden="true">
              <span />
              <span />
              <span />
              <span />
              <span />
            </span>
          </section>
          <p className="pcost-note">
            *Illustrative estimate for {scale}. Actual numbers vary.
          </p>
        </aside>
      </div>
    </Screen>
  );
}
